import React, { useEffect, useRef } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useImmer } from 'use-immer';

import Dropdown from '../Dropdown';
import Pagination from '../Pagination';
import {
  performSearch,
  setSelectedIndex,
  setFilter,
  setUrlParams,
} from '../../slices/appSlice';
import { cn } from '../../../../utils';

const Tabs = ({ children, lists, selectedIndex, maxPages, currentPage, listId, focusFirstCardInNewPage }) => {
  const dispatch = useDispatch();
  const tabsRef = useRef();
  const [showMore, setShowMore] = useImmer(false);
  const [showMobile, setShowMobile] = useImmer(false);

  const { pages = [] } = lists[selectedIndex];

  useEffect(() => {
    function selectedTabChanged(e) {
      const { detail: { index } } = e;
      const { pages = [] } = lists[index];
      dispatch(setSelectedIndex({ index }));
      dispatch(setUrlParams({ get: { cat: lists[index].id } }));

      if (pages.length === 0) {
        dispatch(performSearch({ page: 1 }));
      }
    }

    if (tabsRef && tabsRef.current) {
      tabsRef.current.addEventListener('selected-changed', selectedTabChanged, false);
      return function cleanup() {
        tabsRef.current.removeEventListener('selected-changed', selectedTabChanged, false);
      };
    }
  }, []);

  const dispatchFilter = (payload) => {
    dispatch(setFilter(payload));
  };

  return (
    <ft-tabs class={'product-listing__tabs'} selected-index={selectedIndex}
             ref={tabsRef}>
      {
        lists.map(({ label, filters, query = {} }, l) => {
          const { filterQuery = {} } = query;

          return (
            <React.Fragment key={l}>
              <div className={lists.length === 1 ? 'hidden' : ''}
                   slot="tab">{label}</div>
              <div slot="panel">
                <ft-section spacing="top-small bottom-small" theme="light" content-padding="none">
                  <div className="container container-default product-listing__filters">
                    <div
                      className={cn('relative grid tablet:grid-3 laptop:grid-5 desktop:grid-6 !gap-2xs', !showMobile ? 'hidden' : '')}>
                      {
                        filters.slice(0, 4).map(({ label, items, term }, index) => (
                          <Dropdown
                            key={`${l}${index}`} {...{ label, term, items, index, parentIndex: l, dispatchFilter, filterQuery }} />
                        ))
                      }
                      {
                        filters.length > 5 && !showMore &&
                        <ft-button variant={4} type="button"
                                   onClick={() => setShowMore((draft) => draft = true)}>
                          <ft-icon width="18" name="plus"
                                   slot="start"></ft-icon>
                          {Drupal.t('More filters')}</ft-button>
                      }
                      {
                        (filters.length === 5 || showMore) &&
                        filters.slice(4, filters.length).map(({ label, items, term }, index) => (
                          <Dropdown
                            key={`${l}${4 + index}`} {...{ label, term, items, parentIndex: l, index: 4 + index, dispatchFilter, filterQuery }} />
                        ))
                      }
                    </div>
                    {
                      filters.length > 0 ?
                        <ft-button variant={4} type="button" full={true}
                         class={cn('tablet:hidden', !showMobile ? '' : 'mt-xs')}
                         onClick={() => {
                           setShowMobile((draft) => draft = !showMobile);
                           setShowMore((draft) => draft = true);
                        }}>
                          <ft-icon width="18" name="plus"
                                   slot="start"></ft-icon>
                          {!showMobile ? Drupal.t('Show filter options') : Drupal.t('Hide filter options')}
                        </ft-button> : ''
                    }
                    <div>
                      {
                        Object.keys(filterQuery).map((term) => (
                          filterQuery[term].map((key, i) => (
                            <ft-badge class="cursor-pointer mt-xs mr-xs"
                                      onClick={() => dispatchFilter({ key, term })}
                                      key={`${l}${term}${i}`} type="tag"
                                      variant="1">
                              {key}
                              <ft-button slot="end" ghost="" icon=""
                                         size="small">
                                <ft-icon width="16" color="dark"
                                         name="close"></ft-icon>
                                <span slot="a11y">ally</span>
                              </ft-button>
                            </ft-badge>
                          ))
                        ))
                      }
                      {
                        Object.values(filterQuery).flat().length > 1 ?
                          <ft-badge class="cursor-pointer mt-xs mr-xs"
                                    onClick={() => dispatch(setFilter({ filterQueryOverwrite: {} }))}
                                    type="tag" variant="1">
                            {Drupal.t('Reset all filters')}
                            <ft-button
                              slot="end" ghost="" icon="" size="small">
                              <ft-icon width="16" color="dark"
                                       name="close"></ft-icon>
                              <span slot="a11y">ally</span>
                            </ft-button>
                          </ft-badge>
                          : ''
                      }
                    </div>
                  </div>
                </ft-section>
                <If condition={l === selectedIndex}>
                  {children}
                  <If condition={maxPages > 1}>
                    <div className="flex justify-center my-sm">
                      <Pagination { ...{maxPages, currentPage, listId, focusFirstCardInNewPage}}></Pagination>
                    </div>
                  </If>
                </If>
              </div>
            </React.Fragment>
          );
        })
      }
    </ft-tabs>
  );
};

export default Tabs;
